import {
  COMPANY_FIELD_PREFIX,
  FILTER_CHOICE_OPERATORS,
  FILTER_DATE_OPERATORS,
  FILTER_EMAIL_OPERATORS,
  FILTER_NUMBER_OPERATORS,
  FILTER_TAG_OPERATORS,
  FILTER_TASK_OPERATORS,
  OPPORTUNITY_FILTER_VALUES,
  SEQUENCE_FILTER_VALUES,
  SYSTEM_FILTER_FIELDS,
  type FilterCondition,
  type FilterFieldType,
  type FilterGroupKey,
  type LeadCatalogResponse,
  type OpportunityFilterValue,
  type SequenceFilterValue,
  type SystemFilterField,
} from "@gioia365/contracts";
import type { Translate } from "../../i18n/locale";
import { KNOWN_OUTCOMES, outcomeLabel } from "../lead-display";

// Come si leggono i filtri (piano M3 Task 4, mock-up `FIELDS`, `OPERATORS` e `conditionText`): il nome del campo, gli
// operatori del suo tipo e il riassunto di una condizione sul suo pulsante («Status: In target, Qualificato», «Ultima
// chiamata: ultimi 7 giorni»). Le parole vengono dai cataloghi; qui soltanto come si mettono insieme.

export type FilterField = {
  /** `owner`, `status`, … oppure `field:<chiave>` per un campo dell'azienda. */
  ref: string;
  type: FilterFieldType;
  group: FilterGroupKey;
  label: string;
  /** Il riassunto senza scelta: «tutti» o «tutte», secondo il nome del campo. */
  all: string;
};

export type FilterOperator = FilterCondition["op"];

/** I gruppi del pannello, nell'ordine del mock-up: «Disco, demo e opportunità» (R8 di M6) fra «Salone» e «Provenienza». */
export const FILTER_GROUP_ORDER: readonly FilterGroupKey[] = ["owner", "status", "calls", "territory", "salon", "opportunities", "created"];

/** I campi di sistema il cui nome vuole «tutte»; gli altri dicono «tutti». */
const FEMININE_SYSTEM_FIELDS: ReadonlySet<SystemFilterField> = new Set([
  "nextTaskAt",
  "sequence",
  "latestCallAt",
  "outgoingCallCountLast12Months",
  "outgoingCallCountLast4Months",
  "country",
  "region",
  "province",
  "city",
  "opportunity",
  "createdAt",
]);

/** I campi dell'azienda di partenza il cui nome vuole «tutte» (`DEFAULT_LEAD_FIELDS`); per gli altri «tutti». */
const FEMININE_COMPANY_KEYS: ReadonlySet<string> = new Set(["fiche_media", "presenze_mensili"]);

const SYSTEM_REFS = Object.keys(SYSTEM_FILTER_FIELDS) as SystemFilterField[];

function isSystemRef(ref: string): ref is SystemFilterField {
  return Object.hasOwn(SYSTEM_FILTER_FIELDS, ref);
}

/** Il tipo di filtro di un campo dell'azienda: le scelte, i numeri e gli euro; un testo non si filtra. */
function companyFilterType(type: LeadCatalogResponse["fields"][number]["type"]): FilterFieldType | null {
  if (type === "choice") return "choice";
  if (type === "number" || type === "currency_eur") return "number";
  return null;
}

/**
 * I campi del pannello: quelli di sistema nell'ordine di `SYSTEM_FILTER_FIELDS`, poi i campi dell'azienda a scelta, a
 * numero o in euro nel gruppo «Salone», nell'ordine del catalogo. Senza il catalogo restano quelli di sistema.
 */
export function filterFields(t: Translate, catalog: LeadCatalogResponse | undefined): FilterField[] {
  const system = SYSTEM_REFS.map((ref): FilterField => ({
    ref,
    type: SYSTEM_FILTER_FIELDS[ref].type,
    group: SYSTEM_FILTER_FIELDS[ref].group,
    label: t(`leadFilters.fields.${ref}`),
    all: FEMININE_SYSTEM_FIELDS.has(ref) ? t("leadFilters.allFeminine") : t("leadFilters.all"),
  }));
  const company = (catalog?.fields ?? []).flatMap((field): FilterField[] => {
    const type = companyFilterType(field.type);
    if (type === null) return [];
    const all = FEMININE_COMPANY_KEYS.has(field.key) ? t("leadFilters.allFeminine") : t("leadFilters.all");
    return [{ ref: `${COMPANY_FIELD_PREFIX}${field.key}`, type, group: "salon", label: field.label, all }];
  });
  return [...system, ...company];
}

/** Il nome di un campo; un campo dell'azienda che il catalogo non ha (ancora) mostra la sua chiave. */
export function fieldLabel(t: Translate, ref: string, catalog: LeadCatalogResponse | undefined): string {
  if (isSystemRef(ref)) return t(`leadFilters.fields.${ref}`);
  const key = ref.startsWith(COMPANY_FIELD_PREFIX) ? ref.slice(COMPANY_FIELD_PREFIX.length) : ref;
  return catalog?.fields.find((field) => field.key === key)?.label ?? key;
}

/**
 * Il campo di una condizione: quello del pannello se c'è, altrimenti uno ricostruito dalla condizione (un campo
 * dell'azienda mentre il catalogo arriva), così il filtro attivo si legge comunque.
 */
export function fieldOf(t: Translate, fields: readonly FilterField[], condition: FilterCondition, catalog: LeadCatalogResponse | undefined): FilterField {
  const found = fields.find((field) => field.ref === condition.field);
  if (found) return found;
  return { ref: condition.field, type: condition.kind, group: "salon", label: fieldLabel(t, condition.field, catalog), all: t("leadFilters.all") };
}

/** Gli operatori di un campo, nell'ordine del mock-up. */
export function operatorsFor(field: Pick<FilterField, "type">): readonly FilterOperator[] {
  switch (field.type) {
    case "choice":
      return FILTER_CHOICE_OPERATORS;
    case "tags":
      return FILTER_TAG_OPERATORS;
    case "number":
      return FILTER_NUMBER_OPERATORS;
    case "date":
      return FILTER_DATE_OPERATORS;
    case "task":
      return FILTER_TASK_OPERATORS;
    case "email":
      return FILTER_EMAIL_OPERATORS;
  }
}

/** I periodi «più di … fa»: per «Ultima chiamata» comprendono chi non è mai stato chiamato (piano M3, D2). */
const OLDER_OPERATORS: ReadonlySet<FilterOperator> = new Set(["older7", "older30", "older90", "older180", "older365"]);

const isTaskOperator = (op: FilterOperator): op is (typeof FILTER_TASK_OPERATORS)[number] => (FILTER_TASK_OPERATORS as readonly string[]).includes(op);
const isEmailOperator = (op: FilterOperator): op is (typeof FILTER_EMAIL_OPERATORS)[number] => (FILTER_EMAIL_OPERATORS as readonly string[]).includes(op);
/** Gli operatori con il nome in `leadFilters.ops`: quelli delle scelte, dei tag, dei numeri e delle date. */
type PanelOperator = (typeof FILTER_CHOICE_OPERATORS | typeof FILTER_TAG_OPERATORS | typeof FILTER_NUMBER_OPERATORS | typeof FILTER_DATE_OPERATORS)[number];
const PANEL_OPERATORS: ReadonlySet<string> = new Set([...FILTER_CHOICE_OPERATORS, ...FILTER_TAG_OPERATORS, ...FILTER_NUMBER_OPERATORS, ...FILTER_DATE_OPERATORS]);
const isPanelOperator = (op: FilterOperator): op is PanelOperator => PANEL_OPERATORS.has(op);

/**
 * Il nome di un operatore nel pannello («il giorno…» con i puntini); per «Ultima chiamata» i «più di … fa» dicono «o mai chiamato»;
 * «Prossima task» ha le parole sue (piano M5, Task 20: «scaduta», «oggi», «domani», «entro il giorno…», «nessuna»), e così «Ha
 * cliccato un'email» (ingresso dei Lead, I5: «negli ultimi … giorni», «mai»).
 */
export function operatorLabel(t: Translate, field: Pick<FilterField, "ref">, op: FilterOperator): string {
  if (isEmailOperator(op)) return t(`leadFilters.emailClicked.${op}`);
  if ((field.ref === "nextTaskAt" && isTaskOperator(op)) || !isPanelOperator(op)) return t(`leadFilters.nextTask.${op}`);
  const label = t(`leadFilters.ops.${op}`);
  return field.ref === "latestCallAt" && OLDER_OPERATORS.has(op) ? t("leadFilters.orNeverCalled", { op: label }) : label;
}

/**
 * Il riassunto di una condizione completa, come sul suo pulsante: «In target, Qualificato» (fino a due voci, con «; » se una
 * voce ha già una virgola, poi «3 scelti»), «non è In target», «ha uno di questi Masterclass Colore», «almeno 6», «ultimi 7 giorni», «il giorno
 * 24/09/2026». `labels` dà il nome di ogni voce; una voce senza nome (una città arrivata con un link) si conta soltanto,
 * senza mostrare il codice. `formatDay` scrive un giorno `AAAA-MM-GG` in numeri.
 */
export function conditionText(
  t: Translate,
  field: FilterField,
  condition: FilterCondition,
  labels: ReadonlyMap<string, string>,
  formatDay: (iso: string) => string,
): string {
  const op = operatorLabel(t, field, condition.op);
  switch (condition.kind) {
    case "number":
      return `${op} ${condition.value}`;
    case "date":
    case "task":
      return condition.day === undefined ? op : `${op.replace(/…$/u, "")} ${formatDay(condition.day)}`;
    case "email":
      return condition.days === undefined ? op : t("leadFilters.emailClicked.withinDays", { days: condition.days });
    case "choice":
    case "tags": {
      const names = condition.values.map((value) => labels.get(value));
      const known = names.filter((name): name is string => name !== undefined);
      // Due voci si separano con la virgola, oppure con «; » quando una delle due ne ha già una («non è Lead nuovo, mai
      // chiamato; Finita o ferma»): così due voci non si leggono come tre (questione 269).
      const separator = known.some((name) => name.includes(",")) ? "; " : ", ";
      let text: string;
      if (known.length === names.length && names.length <= 2) text = known.join(separator);
      else text = names.length === 1 ? t("leadFilters.chosenOne") : t("leadFilters.chosen", { count: names.length });
      return condition.kind === "choice" && condition.op === "in" ? text : `${op} ${text}`;
    }
  }
}

const COUNTRY_NAMES = { IT: "leadFilters.countryNames.IT", CH: "leadFilters.countryNames.CH" } as const;

/** Il nome di una nazione per il suo codice; un codice che il catalogo non nomina resta il codice. */
export function countryName(t: Translate, code: string): string {
  return Object.hasOwn(COUNTRY_NAMES, code) ? t(COUNTRY_NAMES[code as keyof typeof COUNTRY_NAMES]) : code;
}

/** Le voci di «Sequenza di chiamate» (piano M5, Task 14), con le parole del mock-up. */
const SEQUENCE_STATE_NAMES = {
  due_today: "leadFilters.sequence.due_today",
  new_never_called: "leadFilters.sequence.new_never_called",
  in_progress_later: "leadFilters.sequence.in_progress_later",
  ended: "leadFilters.sequence.ended",
} as const satisfies Record<SequenceFilterValue, string>;

/** Le voci di «Opportunità» (piano M6, R8, Task 24): «Nessuna» e le fasi, con le parole del mock-up. */
const OPPORTUNITY_STAGE_NAMES = {
  none: "leadFilters.opportunity.none",
  disco_booked: "leadFilters.opportunity.disco_booked",
  disco_held: "leadFilters.opportunity.disco_held",
  demo_booked: "leadFilters.opportunity.demo_booked",
  demo_held: "leadFilters.opportunity.demo_held",
  sold: "leadFilters.opportunity.sold",
  lost: "leadFilters.opportunity.lost",
} as const satisfies Record<OpportunityFilterValue, string>;

/**
 * Il nome di una voce di `GET /api/leads/filter-options`: quello del server, oppure, dove il server lo lascia vuoto,
 * quello dell'interfaccia («Senza owner», gli esiti, «Mai chiamato», gli stati della sequenza, le fasi dell'Opportunità); le
 * nazioni con il loro nome.
 */
export function optionLabel(t: Translate, ref: string, option: { value: string; label: string }): string {
  if (ref === "country") return countryName(t, option.value);
  if (option.label !== "") return option.label;
  if (ref === "owner" && option.value === "none") return t("leadFilters.noOwner");
  if (ref === "latestCallOutcome") return option.value === "none" ? t("leadFilters.outcomeNone") : outcomeLabel(t, option.value);
  if (ref === "sequence" && Object.hasOwn(SEQUENCE_STATE_NAMES, option.value)) return t(SEQUENCE_STATE_NAMES[option.value as SequenceFilterValue]);
  if (ref === "opportunity" && Object.hasOwn(OPPORTUNITY_STAGE_NAMES, option.value)) return t(OPPORTUNITY_STAGE_NAMES[option.value as OpportunityFilterValue]);
  return option.value;
}

/**
 * I nomi delle voci che l'interfaccia conosce senza chiederli al server: gli esiti, gli stati della sequenza, le fasi
 * dell'Opportunità, «Senza owner», le nazioni, le regioni (la voce è già il nome) e le voci dei campi dell'azienda dal catalogo.
 */
export function knownLabels(t: Translate, ref: string, values: readonly string[], catalog: LeadCatalogResponse | undefined): Map<string, string> {
  const labels = new Map<string, string>();
  if (ref === "latestCallOutcome") {
    for (const value of [...KNOWN_OUTCOMES, "none"]) labels.set(value, optionLabel(t, ref, { value, label: "" }));
  } else if (ref === "sequence") {
    for (const value of SEQUENCE_FILTER_VALUES) labels.set(value, optionLabel(t, ref, { value, label: "" }));
  } else if (ref === "opportunity") {
    for (const value of OPPORTUNITY_FILTER_VALUES) labels.set(value, optionLabel(t, ref, { value, label: "" }));
  } else if (ref === "owner") {
    labels.set("none", t("leadFilters.noOwner"));
  } else if (ref === "country" || ref === "region") {
    for (const value of values) labels.set(value, ref === "country" ? countryName(t, value) : value);
  } else if (ref.startsWith(COMPANY_FIELD_PREFIX)) {
    const key = ref.slice(COMPANY_FIELD_PREFIX.length);
    for (const option of catalog?.fields.find((field) => field.key === key)?.options ?? []) labels.set(option.id, option.label);
  }
  return labels;
}
